import React from 'react';
import { Flame, Zap, CheckCircle, Phone, ArrowRight, MapPin, Shield, Clock, TrendingDown, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const SoudageReparationMetallique = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <SEO 
        title="Soudage Industriel et Réparation Métallique sur Site | ORION INDUSTRIE"
        description="Travaux de soudage et réparation métallique sur installations industrielles. Intervention mobile avec poste autonome. Soudage structures, équipements industriels et matériel de chantier."
        keywords="soudage industriel, soudage sur site, réparation métallique, soudage mobile, réparation structure métallique, soudage équipement industriel"
      />
      <Header />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#020d2a] to-slate-800 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-6">
            <Flame className="w-12 h-12 text-[#FF6600]" />
            <h1 className="text-4xl sm:text-5xl font-bold">
              Soudage & Réparation Métallique sur Site
            </h1>
          </div>
          <p className="text-2xl text-orange-400 font-semibold mb-4">
            Intervention mobile • Poste de soudage autonome • Sans démontage
          </p>
          <p className="text-xl text-slate-300 max-w-4xl mb-8">
            Nous intervenons directement chez vous pour réaliser des travaux de soudage industriel 
            et de réparation métallique sur vos structures, équipements et installations.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white group"
            >
              Demander une intervention
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <a href="tel:+33659452562">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 text-white hover:bg-white/10"
              >
                <Phone className="mr-2 w-5 h-5" />
                06 59 45 25 62
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Avantage intervention mobile */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <Zap className="w-12 h-12 text-[#FF6600] mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Intervention Mobile avec Poste Autonome
            </h2>
            <p className="text-xl text-slate-600">
              Nous intervenons directement sur votre site avec un poste de soudage autonome, 
              évitant ainsi le démontage et le transport d'équipements lourds ou volumineux.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield className="w-8 h-8 text-blue-600" />,
                title: 'Pas de démontage',
                desc: 'Intervention directe sur l\'équipement en place, sans nécessité de transport vers un atelier'
              },
              {
                icon: <Clock className="w-8 h-8 text-orange-600" />,
                title: 'Gain de temps',
                desc: 'Réduction des délais d\'immobilisation et remise en service plus rapide'
              },
              {
                icon: <TrendingDown className="w-8 h-8 text-green-600" />,
                title: 'Économie',
                desc: 'Évite les coûts de démontage, transport et remontage des équipements'
              }
            ].map((item, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prestations */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">
            Nos Prestations de Soudage et Réparation
          </h2>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                <Flame className="w-8 h-8 text-[#FF6600]" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Soudage Industriel</h3>
              <p className="text-slate-600 mb-6">
                Travaux de soudage sur structures métalliques et équipements industriels
              </p>
              <ul className="space-y-3">
                {[
                  'Soudage de structures métalliques',
                  'Soudage d\'équipements industriels',
                  'Soudage de matériel de manutention',
                  'Soudage d\'équipements BTP',
                  'Soudage d\'éléments de charpente',
                  'Assemblages métalliques',
                  'Renforcement de structures'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Wrench className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Réparation Métallique</h3>
              <p className="text-slate-600 mb-6">
                Remise en état d'éléments métalliques endommagés ou fissurés
              </p>
              <ul className="space-y-3">
                {[
                  'Réparation de structures fissurées',
                  'Réparation d\'équipements endommagés',
                  'Remise en état d\'éléments cassés',
                  'Réparation de matériel de chantier',
                  'Réparation d\'engins BTP',
                  'Réparation après choc ou accident',
                  'Renforcement de zones fragilisées'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Types d'interventions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">
            Exemples d'Interventions
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: 'Équipements de Quai',
                items: ['Niveleurs de quai', 'Butoirs', 'Protections métalliques', 'Structures de soutien']
              },
              {
                title: 'Structures Industrielles',
                items: ['Charpentes métalliques', 'Châssis d\'équipements', 'Supports et bâtis', 'Passerelles']
              },
              {
                title: 'Matériel de Manutention',
                items: ['Convoyeurs', 'Chariots', 'Supports de stockage', 'Rails et guidages']
              },
              {
                title: 'Installations Fixes',
                items: ['Garde-corps', 'Escaliers', 'Plateformes', 'Protections diverses']
              },
              {
                title: 'Équipements BTP',
                items: ['Engins de chantier', 'Bennes', 'Grues', 'Échafaudages', 'Containers', 'Coffrages métalliques']
              }
            ].map((category, index) => (
              <div key={index} className="bg-slate-50 rounded-xl p-6 hover:shadow-lg transition-shadow">
                <h3 className="text-lg font-bold text-slate-900 mb-4">{category.title}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="text-[#FF6600] font-bold">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-slate-900 text-center mb-12">
            Déroulement d'une Intervention
          </h2>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                step: '1',
                title: 'Évaluation du besoin',
                desc: 'Analyse de la réparation nécessaire, évaluation de la faisabilité technique et conditions d\'intervention'
              },
              {
                step: '2',
                title: 'Préparation',
                desc: 'Mise en sécurité de la zone, préparation des surfaces et organisation de l\'intervention'
              },
              {
                step: '3',
                title: 'Réalisation des travaux',
                desc: 'Soudage et réparation métallique selon les règles de l\'art avec notre poste autonome'
              },
              {
                step: '4',
                title: 'Contrôle et finition',
                desc: 'Vérification de la qualité de la réparation, finition et remise en service'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg flex items-start gap-6">
                <div className="w-12 h-12 bg-[#FF6600] text-white rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0">
                  {item.step}
                </div>
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-orange-50 border-l-4 border-[#FF6600] p-6 rounded-lg max-w-4xl mx-auto mt-12">
            <h3 className="font-bold text-slate-900 mb-2">Important</h3>
            <p className="text-slate-700">
              Les interventions de soudage respectent les consignes de sécurité en vigueur. 
              Nous évaluons systématiquement la faisabilité technique avant toute intervention 
              et nous adaptons aux contraintes de votre site.
            </p>
          </div>
        </div>
      </section>

      {/* Zone d'intervention */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <MapPin className="w-12 h-12 text-[#FF6600] mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-slate-900 mb-6">
              Intervention Mobile en France
            </h2>
            <p className="text-xl text-slate-700 mb-6">
              Basés à <strong>Nevers (Nièvre)</strong>, nous intervenons sur des sites industriels 
              partout en France avec notre poste de soudage autonome.
            </p>
            <p className="text-lg text-slate-600 mb-8">
              La mobilité de nos équipements permet d'intervenir directement sur vos installations, 
              quelle que soit leur localisation. Les conditions d'intervention et les frais de déplacement 
              sont définis dans chaque devis en fonction de votre projet.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="py-20 bg-gradient-to-r from-[#020d2a] to-slate-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">
            Besoin d'une intervention de soudage ou de réparation métallique ?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Contactez-nous pour discuter de votre projet
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.location.href = '/#quote'}
              size="lg"
              className="bg-[#FF6600] hover:bg-[#E65A00] text-white"
            >
              Demander un devis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href="tel:+33659452562">
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10"
              >
                <Phone className="mr-2 w-5 h-5" />
                06 59 45 25 62
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SoudageReparationMetallique;
